
import { Component, Prop, Vue } from 'vue-property-decorator'
import Filelist from '@/components/Filelist.vue'
import MediaItem from '@/types/mediaItem'
import { labelFor, ReadOnlyField, ReadOnlySection, valueOrDash } from '@/views/kam/forms/kamReadOnlyPresenter'

const FieldValue = {
  functional: true,
  props: {
    field: { required: true },
    displayValue: { required: true }
  },
  render (h: any, context: any) {
    const field = context.props.field
    const displayValue = context.props.displayValue

    if (field.kind === 'boolean') {
      const label = displayValue(field.value, field.key)
      return h('a-tag', { props: { color: field.value ? 'green' : 'default' } }, label)
    }

    if (field.kind === 'array') {
      const items = Array.isArray(field.value) ? field.value.map((item: any) => displayValue(item, field.key)).filter((item: string) => item !== '-') : []
      return items.length
        ? h('div', { class: 'value-list' }, items.map((item: string) => h('a-tag', { key: item }, item)))
        : h('span', '-')
    }

    if (field.kind === 'object') {
      const value = field.value
      const items = value && typeof value === 'object' && !Array.isArray(value)
        ? Object.keys(value).filter(key => key !== 'media' && !key.startsWith('_')).map(key => ({ key, label: labelFor(key), value: displayValue(value[key], key) }))
        : []
      return items.length
        ? h('div', { class: 'nested-list' }, items.map((item: any) => h('div', { class: 'nested-row', key: item.key }, [
          h('span', item.label),
          h('strong', item.value)
        ])))
        : h('span', '-')
    }

    return h('span', displayValue(field.value, field.key))
  }
}

@Component({
  name: 'KamReadOnlyForm',
  components: { Filelist, 'field-value': FieldValue }
})
export default class KAMReadOnlyForm extends Vue {
  @Prop({ required: true }) title!: string
  @Prop({ required: true }) answers!: Record<string, any>
  @Prop({ default: () => [] }) sections!: ReadOnlySection[]
  @Prop({ default: () => ({}) }) media!: Record<string, any>
  @Prop({ default: () => [] }) siteManagers!: any[]
  @Prop({ default: () => [] }) projectLeaders!: any[]
  @Prop({ default: () => [] }) constructionWorkers!: any[]
  @Prop({ default: () => [] }) employees!: any[]

  get renderSections () : ReadOnlySection[] {
    if (this.sections && this.sections.length && this.sectionsHaveContent(this.sections)) return this.sections
    const rawFields = Object.keys(this.answers || {})
      .filter(key => key !== 'media' && !key.startsWith('_'))
      .map(key => this.fieldFor(key, this.answers[key]))
    const fields = rawFields.filter(field => field.kind !== 'table')
    const tables = rawFields.filter(field => field.kind === 'table')
    if (fields.length || tables.length) {
      return [{ key: 'answers', title: 'Antwoorden', fields, tables }]
    }
    return []
  }

  sectionsHaveContent (sections: ReadOnlySection[]) : boolean {
    return sections.some(section => Boolean(
      (section.fields && section.fields.length) ||
      (section.tables && section.tables.length)
    ))
  }

  get mediaSections () : Array<{ key: string, items: MediaItem[] }> {
    const source = this.media && Object.keys(this.media).length ? this.media : this.answers?.media || {}
    return Object.keys(source || {})
      .map(key => ({ key, items: this.mediaList(source[key]) }))
      .filter(section => section.items.length > 0)
  }

  get peopleMap () : Record<string, string> {
    const map: Record<string, string> = {}
    ;[...this.siteManagers, ...this.projectLeaders, ...this.constructionWorkers, ...this.employees].forEach((person: any) => {
      if (person && person.id != null) map[String(person.id)] = person.label || person.full_name || person.name || String(person.id)
    })
    return map
  }

  fieldFor (key: string, value: any) : ReadOnlyField {
    if (typeof value === 'boolean') {
      return { key, label: this.labelFor(key), kind: 'boolean', value }
    }

    if (Array.isArray(value)) {
      if (value.every(item => item && typeof item === 'object' && !Array.isArray(item))) {
        const columns = this.tableColumns(value)
        return { key, label: this.labelFor(key), kind: 'table', rows: value, columns }
      }
      return { key, label: this.labelFor(key), kind: 'array', value }
    }

    if (value && typeof value === 'object') {
      return { key, label: this.labelFor(key), kind: 'object', value }
    }

    return { key, label: this.labelFor(key), kind: 'text', value: this.displayValue(value, key) }
  }

  tableColumns (rows: Record<string, any>[]) : Array<{ key: string, label: string }> {
    const keys: string[] = []
    rows.forEach(row => {
      Object.keys(row || {}).forEach(key => {
        if (!key.startsWith('_') && !keys.includes(key)) keys.push(key)
      })
    })
    return keys.map(key => ({ key, label: this.labelFor(key) }))
  }

  mediaList (value: any) : MediaItem[] {
    if (Array.isArray(value)) return value.filter(item => item && item.id)
    return value && value.id ? [value] : []
  }

  displayValue (value: any, key = '') : string {
    if (value === null || typeof value === 'undefined' || value === '') return '-'
    if (typeof value === 'boolean') return value ? 'Ja' : 'Nee'
    if (Array.isArray(value)) {
      const values = value.map(item => this.displayValue(item, key)).filter(item => item !== '-')
      return values.length ? values.join(', ') : '-'
    }
    if (value && typeof value === 'object') {
      if (value.label || value.full_name || value.name) return value.label || value.full_name || value.name
      return Object.keys(value)
        .map(childKey => `${this.labelFor(childKey)}: ${this.displayValue(value[childKey], childKey)}`)
        .join(', ')
    }
    const stringValue = String(value)
    if (this.peopleMap[stringValue] && this.looksLikePersonField(key)) return this.peopleMap[stringValue]
    return valueOrDash(stringValue)
  }

  isTagValue (value: any) : boolean {
    return value && typeof value === 'object' && !Array.isArray(value) && value.kind === 'tag' && value.label && value.color
  }

  looksLikePersonField (key: string) : boolean {
    return /(user|person|people|manager|leader|worker|party|parties|witness|responsible|coordinator|present|injured)/i.test(key)
  }

  labelFor (key: string) : string {
    return labelFor(key)
  }
}
